import { useState, useEffect, useCallback } from "react";
import BackMethods from "../../back_component/BackMethods";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowRight, FaUserShield } from "react-icons/fa";
import { can, PERMISSIONS } from "../../auth/permissions";
import "../../styles/admin-shared.css";
import "../Users/users-admin.css";
import "./roles-admin.css";
import RolesManagementModals from "./RolesManagementModals";

export default function RolesManagementPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { backEnd_request } = BackMethods();

    const [roles, setRoles] = useState([]);
    const [filteredRoles, setFilteredRoles] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [isLoading, setIsLoading] = useState(true);

    const [modal, setModal] = useState(null);
    const [modalRoleId, setModalRoleId] = useState(null);

    const [flash, setFlash] = useState(null);

    const canCreate = can(PERMISSIONS.ROLES_CREATE);
    const canEdit = can(PERMISSIONS.ROLES_EDIT);
    const canDelete = can(PERMISSIONS.ROLES_DELETE);
    const hasAnyAction = canEdit || canDelete;

    const fetchRoles = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await backEnd_request.get("/admin/Roles");
            const data = res.data.roles || res.data || [];
            setRoles(data);
            setFilteredRoles(data);
        } catch (error) {
            console.error(error);
            setRoles([]);
            setFilteredRoles([]);
        } finally {
            setIsLoading(false);
        }
    }, [backEnd_request]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    useEffect(() => {
        if (location.state?.successMessage) {
            setFlash({ message: location.state.successMessage, variant: "success" });
            navigate(".", { replace: true, state: {} });
        }
    }, [location.state, navigate]);

    useEffect(() => {
        if (!flash?.message) return undefined;
        const t = setTimeout(() => setFlash(null), 4500);
        return () => clearTimeout(t);
    }, [flash]);

    const handleSearch = (term, type) => {
        setSearchTerm(term);
        setFilterType(type);
        const searchVal = (term || "").toLowerCase();
        const filtered = roles.filter((role) => {
            if (type === "name") return role.name?.toLowerCase().includes(searchVal);
            if (type === "displayName") return role.displayName?.toLowerCase().includes(searchVal);
            return (
                role.name?.toLowerCase().includes(searchVal) ||
                role.displayName?.toLowerCase().includes(searchVal) ||
                role.description?.toLowerCase().includes(searchVal)
            );
        });
        setFilteredRoles(filtered);
    };

    const closeModal = () => {
        setModal(null);
        setModalRoleId(null);
    };

    const onModalSaved = ({ message, variant } = {}) => {
        fetchRoles();
        setFlash({
            message: message || "تم حفظ التغييرات بنجاح",
            variant: variant || "success",
        });
    };

    const openCreate = () => setModal("create");
    const openEdit = (roleId) => {
        setModalRoleId(roleId);
        setModal("edit");
    };

    const handleDelete = async (roleId) => {
        if (!window.confirm("هل أنت متأكد من حذف هذا الدور؟")) return;
        try {
            await backEnd_request.delete(`/admin/Roles/${roleId}`);
            await fetchRoles();
            setFlash({ message: "تم حذف الدور بنجاح.", variant: "success" });
        } catch (error) {
            console.error(error);
            setFlash({
                message: error.response?.data?.message || "تعذر حذف الدور. حاول مرة أخرى.",
                variant: "danger",
            });
        }
    };

    const handleToggleActive = async (roleId, isActive) => {
        const willBeActive = !isActive;
        const confirmText = willBeActive
            ? "هل تريد تفعيل هذا الدور؟"
            : "هل تريد إلغاء تفعيل هذا الدور؟";
        if (!window.confirm(confirmText)) return;
        try {
            await backEnd_request.patch(`/admin/Roles/${roleId}/toggle-active`, willBeActive);
            await fetchRoles();
            setFlash({
                message: willBeActive ? "تم تفعيل الدور بنجاح." : "تم إلغاء تفعيل الدور بنجاح.",
                variant: "success",
            });
        } catch (error) {
            console.error(error);
            setFlash({
                message: error.response?.data?.message || "تعذر تغيير حالة الدور. حاول مرة أخرى.",
                variant: "danger",
            });
        }
    };

    return (
        <div className="users-page-minimal users-admin-page" style={{ direction: "rtl", fontFamily: "Cairo, sans-serif" }}>
            <div className="container pb-5 pt-4 px-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 pb-3 border-bottom gap-3">
                    <div>
                        <h3 className="fw-bold text-primary mb-1 d-flex align-items-center gap-2">
                            <span className="rounded-3 bg-primary bg-opacity-10 p-2 d-inline-flex text-primary">
                                <FaUserShield />
                            </span>
                            إدارة الأدوار
                        </h3>
                        <p className="text-muted small mb-0">التحكم في المسميات والصلاحيات الوظيفية</p>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {canCreate && (
                            <button type="button" className="btn btn-primary d-flex align-items-center px-4 rounded-pill shadow-sm" onClick={openCreate}>
                                <i className="bi bi-plus-lg ms-2"></i> إضافة دور
                            </button>
                        )}
                        <button type="button" onClick={() => navigate("/admin")} className="btn btn-outline-secondary rounded-pill">
                            <FaArrowRight className="ms-1" /> رجوع
                        </button>
                    </div>
                </div>

                {flash?.message ? (
                    <div
                        className={`users-admin-alert px-3 py-2 mb-3 d-flex align-items-center justify-content-between gap-2 ${flash.variant === "danger" ? "users-admin-alert--danger" : ""}`}
                        role="alert"
                    >
                        <span>{flash.message}</span>
                        <button type="button" className={`btn btn-sm btn-link p-0 ${flash.variant === "danger" ? "text-danger" : "text-success"}`} onClick={() => setFlash(null)}>
                            إغلاق
                        </button>
                    </div>
                ) : null}

                <div className="users-toolbar mb-3">
                    <div className="users-count-chip">
                        إجمالي الأدوار
                        <span>{roles.length}</span>
                    </div>
                    <div className="users-search-wrap">
                        <div className="bg-white border rounded-4 p-2 shadow-sm">
                            <div className="row g-2 align-items-center">
                                <div className="col-md-9 border-end-md">
                                    <div className="input-group">
                                        <span className="input-group-text bg-transparent border-0 text-muted">
                                            <i className="bi bi-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-0 shadow-none py-2"
                                            placeholder="ابحث في الأدوار..."
                                            value={searchTerm}
                                            onChange={(e) => handleSearch(e.target.value, filterType)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select form-select-sm border-0 shadow-none fw-medium"
                                        value={filterType}
                                        onChange={(e) => handleSearch(searchTerm, e.target.value)}
                                    >
                                        <option value="all">بحث شامل</option>
                                        <option value="name">الاسم التقني</option>
                                        <option value="displayName">الاسم الظاهر</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="users-table-shell">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border spinner-border-sm text-secondary" />
                        </div>
                    ) : filteredRoles.length === 0 ? (
                        <div className="empty-state">
                            <i className="bi bi-inbox fs-4 mb-2" />
                            <div>لا يوجد أدوار مطابقة للبحث</div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table users-table align-middle mb-0 users-table--roles">
                                <thead>
                                    <tr>
                                        <th className="text-center col-index">#</th>
                                        <th className="text-end users-role-display-head">الاسم الظاهر</th>
                                        <th className="text-end users-role-name-head">اسم الدور</th>
                                        <th className="text-center">مستخدم نظام</th>
                                        <th className="text-center">الصلاحيات</th>
                                        <th className="text-center">الحالة</th>
                                        {hasAnyAction && <th className="text-center">الإجراءات</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRoles.map((role, index) => (
                                        <tr
                                            key={role.roleId}
                                            title={role.description || ""}
                                            onDoubleClick={() => {
                                                setModal("details");
                                                setModalRoleId(role.roleId);
                                            }}
                                        >
                                            <td className="text-center col-index fw-semibold text-muted">{index + 1}</td>
                                            <td className="text-end users-role-display-cell">
                                                <span className="user-name">{role.displayName || "-"}</span>
                                            </td>
                                            <td className="text-end users-role-name-cell">
                                                <span className="user-name">{role.name || "-"}</span>
                                            </td>
                                            <td className="text-center users-role-system-cell">
                                                <span className={`system-pill ${role.isSystemRole ? "system-pill--yes" : "system-pill--no"}`}>
                                                    {role.isSystemRole ? "نظام" : "غير نظام"}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className="role-pill">{role.permissions?.length || 0}</span>
                                            </td>
                                            <td className="text-center">
                                                <span className={`status-pill ${role.isActive ? "status-active" : "status-inactive"}`}>
                                                    {role.isActive ? "نشط" : "معطل"}
                                                </span>
                                            </td>
                                            {hasAnyAction && (
                                                <td className="text-center">
                                                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                                                        {canEdit && (
                                                            <button type="button" className="btn-minimal" title="تعديل" onClick={() => openEdit(role.roleId)}>
                                                                <i className="bi bi-pencil" />
                                                            </button>
                                                        )}
                                                        {canEdit && (
                                                            <button
                                                                type="button"
                                                                className={`btn-minimal ${role.isActive ? "active" : ""}`}
                                                                title="تغيير الحالة"
                                                                onClick={() => handleToggleActive(role.roleId, role.isActive)}
                                                            >
                                                                <i className={`bi ${role.isActive ? "bi-lock" : "bi-unlock"}`} />
                                                            </button>
                                                        )}
                                                        {canDelete && !role.isSystemRole ? (
                                                            <button type="button" className="btn-minimal text-danger-hover" title="حذف" onClick={() => handleDelete(role.roleId)}>
                                                                <i className="bi bi-trash3" />
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <RolesManagementModals
                    modal={modal}
                    roleId={modalRoleId}
                    onClose={closeModal}
                    onSaved={onModalSaved}
                    canCreate={canCreate}
                    canEdit={canEdit}
                />
            </div>
        </div>
    );
}
